import React, { useRef, useEffect, useContext } from "react";
import { Copy, Info } from "lucide-react";
import { ErrorModalContext } from "../../../AlertsModal/AlertsModal";
import ToolTip from "../../../ToolTip/ToolTip";
import { TextareaProps } from "../interfaces";

export const Textarea: React.FC<TextareaProps> = (props) => {
  const {
    autoResize = true,
    className,
    disabled,
    error,
    id,
    isCopyable,
    isRequired,
    label,
    onChange,
    onKeyDown,
    placeholder = "Escribe algo",
    rightAdornment,
    tooltipText,
    value,
  } = props;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { showAlert } = useContext(ErrorModalContext);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const newValue: string = e.target.value;
    onChange(newValue);

    if (autoResize && textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  const copyValue = (): void => {
    if (navigator && navigator.clipboard && value) {
      navigator.clipboard.writeText(value.toString());
      showAlert(
        `${label || "Valor"} copiado correctamente`,
        "success",
        "Copiado al portapapeles",
      );
    }
  };

  useEffect(() => {
    if (autoResize && textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, []);

  return (
    <div className="flex w-full flex-col gap-2">
      {label && (
        <div className="flex items-center gap-2">
          <label className="text-sm font-normal text-gray-500">
            {label}{" "}
            {isRequired && (
              <span className="text-xs font-normal text-gray-500">*</span>
            )}
          </label>
          {tooltipText && (
            <ToolTip text={tooltipText}>
              <Info className="h-3 w-3 shrink-0 text-gray-500" />
            </ToolTip>
          )}
        </div>
      )}
      <div className="relative flex-grow">
        <textarea
          id={id}
          ref={textareaRef}
          className={`w-full rounded-md border border-gray-200 bg-white px-3 py-2 align-top text-sm placeholder:text-gray-500 focus:outline-none disabled:bg-gray-300/20 disabled:text-gray-500 disabled:opacity-100 ${
            className ? className : ""
          } ${isCopyable || rightAdornment ? "pr-9" : ""} ${
            !autoResize ? "resize-none" : ""
          }`}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          disabled={isCopyable || disabled}
        />
        {isCopyable && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <Copy
              className="h-4 w-4 cursor-pointer text-gray-500"
              onClick={copyValue}
            />
          </div>
        )}
        {rightAdornment && !isCopyable && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {rightAdornment}
          </div>
        )}
      </div>
      {error && <label className="text-xs text-red-500">{error}</label>}
    </div>
  );
};
